/** App-agnostic WizButler app switcher */
import axios from "axios";
import { ArrowUpRight, LayoutGrid } from "lucide-react";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { roles } from "resbutler-utils";
import { AppCode } from "resbutler-utils/types/Access";
import { ConfigByClient } from "resbutler-utils/types/Endpoints";
import { WizUser } from "resbutler-utils/types/WizUser";
import { catchExceptionCallback } from "../error";
import { getConfigUI } from "../init";


const isLocalhost = () => window.location.hostname === "localhost";

// Registry of switchable apps keyed by grant AppCode
interface SwitchApp {
  code: AppCode; // grant code + self-exclusion key
  key: string; // spinner/loading id
  name: string;
  badge: string;
  devHost?: string;
  prodHost?: (c: ConfigByClient) => string | undefined;
  hash?: (activeRestaurant: string) => string;
  voice?: boolean;
}

const APPS: SwitchApp[] = [
  { code: "ms", key: "maestro", name: "Maestro", badge: "b-maestro", devHost: "http://localhost:3000", prodHost: (c) => c.host, hash: (r) => `/bookings#${moment().format("YYYYMMDD")}/${r}` },
  { code: "mk", key: "marketing", name: "Marketing", badge: "b-marketing", devHost: "http://localhost:3009", prodHost: (c) => c.revenuePath },
  { code: "of", key: "office", name: "Office", badge: "b-office", devHost: "http://localhost:3002", prodHost: (c) => c.officePath },
  { code: "ac", key: "accounting", name: "Accounting", badge: "b-accounting", devHost: "http://localhost:3012", prodHost: (c) => c.accountingPath },
  { code: "pd", key: "operations", name: "Operations", badge: "b-operations", devHost: "http://localhost:3008", prodHost: (c) => c.omsPath },
  { code: "rp", key: "reporting", name: "Reports & Insights", badge: "b-reporting", devHost: "http://localhost:3011", prodHost: (c) => c.reportingPath },
  { code: "vc", key: "voice", name: "Voice", badge: "b-voice", voice: true },
];

interface AppSwitcherProps {
  user: WizUser; // the signed-in user
  hostApp: AppCode; // app rendering this switcher (no point opening itself)
}

const AppSwitcher: React.FC<AppSwitcherProps> = ({ user, hostApp }) => {
  const [open, setOpen] = useState(false);
  const [loadingApp, setLoadingApp] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const config = getConfigUI() as ConfigByClient;
  const claims = user?.customClaims || ({} as WizUser["customClaims"]);
  const isSystem = roles.hasRole(claims.roles ?? 0, roles.roles.SYSTEM) !== 0;
  const grants = claims.grants || {};
  const activeRestaurant = claims.activeRestaurant || "";
  const hasApp = (code: AppCode) => isSystem || (grants[code] || 0) > 0;

  // SSO: fetch a one-time custom token, then open the target app's /challenge in a new tab.
  const openWithCustomToken = async (key: string, targetHost: string, devHost: string) => {
    try {
      setLoadingApp(key);
      const { data: customToken } = await axios.get(`https://us-central1-${config.config.projectId}.cloudfunctions.net/auth/getCustomToken`, { headers: { "client-id": config.client } });
      const host = isLocalhost() ? devHost : targetHost;
      window.open(`${host}/challenge?ct=${customToken}&clientId=${config.client}`, "_blank");
    } catch (error) {
      catchExceptionCallback(error);
    } finally {
      setLoadingApp(null);
    }
  };

  // Voice has no challenge page — the maestros API returns a ready-to-open challenge_url.
  const openVoice = async () => {
    try {
      setLoadingApp("voice");
      const {
        data: { challenge_url },
      } = await axios.post(`${config.functions}/maestros/voice-app`, { restaurantId: activeRestaurant });
      window.open(challenge_url, "_blank");
    } catch (error) {
      catchExceptionCallback(error);
    } finally {
      setLoadingApp(null);
    }
  };

  const openApp = (app: SwitchApp) => {
    if (app.voice) return openVoice();
    const prod = app.prodHost?.(config) || "";
    const dev = app.devHost || prod;
    const suffix = app.hash ? app.hash(activeRestaurant) : "";
    openWithCustomToken(app.key, `${prod}${suffix}`, `${dev}${suffix}`);
  };

  // Every app except the host, that the user is granted, and that has a target for the current environment.
  const resolvable = (a: SwitchApp) => a.voice || (isLocalhost() ? !!a.devHost : !!a.prodHost?.(config));
  const visible = APPS.filter((a) => a.code !== hostApp && hasApp(a.code) && resolvable(a));

  return (
    <div className="ru-apps-trigger" ref={ref}>
      <button type="button" className="ru-icon-btn" aria-label="Switch app" onClick={() => setOpen((v) => !v)}>
        <LayoutGrid size={18} strokeWidth={1.8} />
      </button>

      {open && (
        <div className="ru-apps-popover" onClick={(e) => e.stopPropagation()}>
          <div className="ru-apps-popover-label">Switch app</div>
          {visible.map((app) => (
            <button key={app.code} type="button" className="ru-app-link" onClick={() => openApp(app)} disabled={loadingApp === app.key}>
              <span className={`ru-app-badge ${app.badge}`}>wb</span>
              <span className="ru-app-name">{app.name}</span>
              {loadingApp === app.key && <span className="ru-app-spinner" />}
              <ArrowUpRight size={12} strokeWidth={2} className="ru-app-arrow" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AppSwitcher;
